import moment from 'moment';
import type { CalendarEvent } from '../../components/shared/Calendar/calendarModel';
import type { DeadlineRow } from './types';
import { getDisplayType } from './activityMeta';

type Translator = (key: string, fallback: string, options?: Record<string, unknown>) => string;

// Mirrors activityMeta.urgencyTone: overdue = danger, within a week = warn, else muted.
const toneOf = (daysLeft: number): CalendarEvent['badgeTone'] =>
  daysLeft < 0 ? 'danger' : daysLeft <= 7 ? 'warn' : 'muted';

// `count` makes i18next resolve the `_one` / `_other` plural forms. The Swedish source copy uses
// the invariant abbreviation "dgr", so the inline fallbacks below are correct for every value —
// but English, Danish and Norwegian all inflect (day/days, dag/dage, dag/dager), so the translated
// keys must be plural pairs. Keying on an invariant source language would have shipped "1 days".
const badgeText = (daysLeft: number, t: Translator): string =>
  daysLeft < 0 ? t('calendar.badge.overdue', '{{count}} dgr sen', { count: Math.abs(daysLeft) })
  : daysLeft === 0 ? t('calendar.badge.today', 'Idag')
  : t('calendar.badge.daysLeft', '{{count}} dgr', { count: daysLeft });

// The type label is not carried on the event: Calendar resolves it through its `labelOf` prop at
// render time, so duplicating it here produced a field nothing ever read. Dropping it also removes
// this file's TYPE_LABELS lookup entirely — one fewer place that can be handed a type the frontend
// does not know about.
export const deadlineRowsToEvents = (rows: DeadlineRow[], t: Translator): CalendarEvent[] =>
  rows.map((row) => ({
    id: row.id,
    date: moment(row.deadline).format('YYYY-MM-DD'),
    typeKey: getDisplayType(row),
    title: row.description,
    badge: badgeText(row.daysLeft, t),
    badgeTone: toneOf(row.daysLeft),
  }));
